import { notification } from "antd";
import { apiFetch, getToken } from "./client";
import { adaptCase } from "./adapters";
import { isDicomEntryName, isZipName } from "../utils/dicomFilenames";

export const listCases = async (filters = {}) => {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v) qs.append(k, v);
  });
  const query = qs.toString();
  const list = await apiFetch(`/api/cases${query ? `?${query}` : ""}`);
  return list.map(adaptCase);
};

export const getCase = async (id) => adaptCase(await apiFetch(`/api/cases/${id}`));

export const createCase = async ({ pathology, organ, region, clinicalSummary, course }) =>
  adaptCase(
    await apiFetch("/api/cases", {
      method: "POST",
      body: {
        pathology,
        organ: organ || "",
        region,
        clinical_summary: clinicalSummary || "",
        course_id: course || null,
      },
    })
  );

export const updateCase = async (id, { pathology, organ, region, clinicalSummary, course }) =>
  adaptCase(
    await apiFetch(`/api/cases/${id}`, {
      method: "PUT",
      body: {
        pathology,
        organ,
        region,
        clinical_summary: clinicalSummary,
        course_id: course,
      },
    })
  );

export const deleteCase = (id) => apiFetch(`/api/cases/${id}`, { method: "DELETE" });

// Multipart image upload — enqueues a background ingestion job, returns the job.
// Uses XHR so we can report upload progress (fetch can't, for uploads).
export const uploadCaseImage = (caseId, { modality, name = "", orderIndex, file, onProgress }) =>
  new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("modality", modality);
    fd.append("name", name);
    if (orderIndex != null) fd.append("order_index", String(orderIndex));
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${process.env.REACT_APP_API_BASE || ""}/api/cases/${caseId}/images`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve(null); }
      } else {
        let detail = "Échec du téléversement";
        try { detail = JSON.parse(xhr.responseText).detail || detail; } catch (_) {}
        reject(new Error(detail));
      }
    };
    xhr.onerror = () => reject(new Error("Échec du téléversement (réseau)"));
    xhr.send(fd);
  });

// Attach an existing reusable asset to a case.
export const attachAsset = (caseId, assetId, orderIndex) =>
  apiFetch(`/api/cases/${caseId}/assets`, {
    method: "POST",
    body: { asset_id: assetId, order_index: orderIndex ?? 0 },
  });

// Detach an image (asset) from a case (the shared asset itself is preserved).
export const detachImage = (caseId, assetId) =>
  apiFetch(`/api/cases/${caseId}/images/${assetId}`, { method: "DELETE" });

export const getJob = (jobId) => apiFetch(`/api/jobs/${jobId}`);

// Poll a set of ingestion jobs until all are done/error (or timeout).
// Returns { done: [...], failed: [...] }.
export const pollJobs = (jobIds, { intervalMs = 1500, timeoutMs = 600000 } = {}) => {
  const pending = new Set(jobIds);
  const done = [];
  const failed = [];
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = async () => {
      await Promise.all(
        [...pending].map(async (id) => {
          try {
            const job = await getJob(id);
            if (job.status === "done") { pending.delete(id); done.push(job); }
            else if (job.status === "error") { pending.delete(id); failed.push(job); }
          } catch (_) {
            pending.delete(id);
            failed.push({ id });
          }
        })
      );
      if (pending.size === 0 || Date.now() - start > timeoutMs) {
        resolve({ done, failed });
      } else {
        setTimeout(tick, intervalMs);
      }
    };
    tick();
  });
};

// Upload the files picked in the case form's modality fields; returns job ids.
const IMAGE_FIELDS = [
  ["macro", "macroImages"],
  ["radio", "radioImages"],
  ["histo", "histoImages"],
];

const extOf = (name = "") => {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
};

const VALID_EXTS = {
  macro: [".png", ".jpg", ".jpeg"],
  histo: [".svs", ".tiff", ".tif", ".ndpi", ".mrxs", ".scn", ".vms", ".vmu", ".png", ".jpg", ".jpeg"],
};

const ACCEPTED_HINT = {
  macro: VALID_EXTS.macro.join(", "),
  radio: ".zip d'un dossier d'examen, ou des fichiers DICOM (.dcm, .ima, ou sans extension)",
  histo: VALID_EXTS.histo.join(", "),
};

// Radiology can't use a fixed extension list: PACS exports are usually
// extensionless or named after the SOP Instance UID. Defer to the shared rule.
const isAcceptedFile = (modality, name) =>
  modality === "radio"
    ? isZipName(name) || isDicomEntryName(name)
    : VALID_EXTS[modality].includes(extOf(name));

const notifyProgress = (key, label, pct) =>
  notification.open({
    key,
    message: `Téléversement : ${label}`,
    description: `${pct}%`,
    duration: 0,
    placement: "bottomRight",
  });

const uploadWithProgress = async (caseId, modality, name, file, jobIds) => {
  const key = `up-${name}-${Date.now()}`;
  notifyProgress(key, name, 0);
  try {
    const job = await uploadCaseImage(caseId, {
      modality,
      name,
      file,
      onProgress: (pct) => notifyProgress(key, name, pct),
    });
    if (job?.id) jobIds.push(job.id);
  } finally {
    notification.destroy(key);
  }
};

const enqueueCaseFiles = async (caseId, values) => {
  const jobIds = [];
  for (const [modality, field] of IMAGE_FIELDS) {
    const files = (values?.[field] || [])
      .map((f) => f?.originFileObj || f)
      .filter((f) => f && f.name);

    // Validate extensions per modality; skip invalid files with a warning.
    const valid = [];
    for (const file of files) {
      if (!isAcceptedFile(modality, file.name)) {
        notification.warning({
          message: `Fichier ignoré : ${file.name}`,
          description: `Formats acceptés (${modality}) : ${ACCEPTED_HINT[modality]}`,
        });
      } else {
        valid.push(file);
      }
    }

    // Loose DICOM instances: bundle all .dcm files into one zip client-side
    // (the backend ingests zips and splits them per series).
    if (modality === "radio") {
      const zips = valid.filter((f) => isZipName(f.name));
      const instances = valid.filter((f) => !isZipName(f.name));
      if (instances.length) {
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();
        instances.forEach((f) => {
          // webkitRelativePath is set when the file came from a folder pick;
          // keeping it preserves the exam's structure inside the archive.
          zip.file(f.webkitRelativePath || f.name, f);
        });
        const key = `zip-${caseId}-${Date.now()}`;
        notification.open({
          key,
          message: "Préparation de l'archive DICOM",
          description: `0 % (${instances.length} fichiers)`,
          duration: 0,
          placement: "bottomRight",
        });
        let blob;
        try {
          blob = await zip.generateAsync(
            // STORE: DICOM pixel data is already compressed, so deflating a
            // large exam in the browser costs seconds for ~1% of size.
            { type: "blob", compression: "STORE" },
            ({ percent }) =>
              notification.open({
                key,
                message: "Préparation de l'archive DICOM",
                description: `${Math.round(percent)} % (${instances.length} fichiers)`,
                duration: 0,
                placement: "bottomRight",
              })
          );
        } finally {
          notification.destroy(key);
        }
        const bundle = new File([blob], "serie-dicom.zip", { type: "application/zip" });
        await uploadWithProgress(caseId, modality, bundle.name, bundle, jobIds);
      }
      for (const file of zips) {
        await uploadWithProgress(caseId, modality, file.name, file, jobIds);
      }
    } else {
      for (const file of valid) {
        await uploadWithProgress(caseId, modality, file.name, file, jobIds);
      }
    }
  }
  return jobIds;
};

// Create a case and enqueue its images. Returns { case, jobIds } — images are
// processed in the background; poll jobIds then refetch the case.
export const createCaseWithImages = async (values) => {
  const created = await createCase(values);
  const jobIds = await enqueueCaseFiles(created.key, values);
  return { case: created, jobIds };
};

// Update a case and enqueue any newly added images. Returns { case, jobIds }.
export const updateCaseWithImages = async (id, values) => {
  const updated = await updateCase(id, values);
  const jobIds = await enqueueCaseFiles(id, values);
  return { case: updated, jobIds };
};
